import { Link } from '@inertiajs/react'
import { Page, PageHeader } from '@/components/page'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { Pagination, PaginationMeta } from '@/components/shared/Pagination'
import { Button } from '@/components/ui/button'
import { Tip } from '@/components/sketch/Tip'
import { List, ListEmpty, ListRow, stageStatus } from '@/components/ui/list'
import { useBasePath } from '@/lib/paths'
import { BusinessSummary } from '@/types'
import { Briefcase, Plus } from 'lucide-react'

type BusinessRow = Omit<BusinessSummary, 'email'> & {
  formation_stage?: string | null
  formation_entity_name?: string | null
  formation_resume?: {
    title: string
    label: string
    context: string
    href: string
  } | null
}

type Props = {
  businesses: BusinessRow[]
  pagination: PaginationMeta
}

export default function BusinessesIndex({ businesses, pagination }: Props) {
  const basePath = useBasePath()

  return (
    <DashboardLayout
      title="Businesses"
      breadcrumbs={[{ label: 'Businesses' }]}
      tips={
        <Tip seed="businesses-intro" title="What a business is here">
          Each company keeps its formation filing, registered agent details and documents together.
        </Tip>
      }
    >
      <Page>
        <PageHeader
          title={<>Businesses</>}
          subtitle={<>Manage your businesses</>}
          actions={
            <Button asChild size="sm">
              <Link href={`${basePath}/formations/new`}>
                <Plus className="w-3.5 h-3.5 mr-1.5" />
                Add a company
              </Link>
            </Button>
          }
        />

        {businesses.length === 0 ? (
          <ListEmpty
            icon={<Briefcase className="w-6 h-6" />}
            title="No businesses yet"
            description="Form a new company and keep its filing, registered agent details and documents together."
            action={{ label: 'Start a company', href: `${basePath}/formations/new` }}
          />
        ) : (
          <List>
            {businesses.map((business) => {
              const resume = business.formation_resume
              return (
                <ListRow
                  key={business.id}
                  href={resume?.href || `${basePath}/businesses/${business.id}`}
                  title={resume?.title || business.name}
                  subtitle={
                    resume
                      ? `Resume · ${resume.label} · ${resume.context}`
                      : business.formation_entity_name || 'Company'
                  }
                  status={
                    business.formation_stage
                      ? stageStatus(business.formation_stage, 'formation')
                      : undefined
                  }
                />
              )
            })}
          </List>
        )}

        <Pagination pagination={pagination} />
      </Page>
    </DashboardLayout>
  )
}
